<template>
  <div class="search-bar" :class="{ 'active-search': isActive }">
    <div class="search-input-container">
      <i class="fas fa-search search-icon"></i>
      <input 
        type="text" 
        :value="modelValue" 
        @input="$emit('update:modelValue', $event.target.value); $emit('input', $event)"
        placeholder="Search by ID, course name..." 
        class="search-input"
      />
      <button 
        v-if="modelValue" 
        class="clear-search-btn" 
        @click="$emit('update:modelValue', ''); $emit('reset')"
        title="Clear search"
      >
        &times;
      </button>
    </div>
    <div class="search-filters">
      <select 
        :value="field" 
        @change="$emit('update:field', $event.target.value)" 
        class="search-select"
      >
        <option value="all">All Fields</option>
        <option value="id">ID</option>
        <option value="course_id">Course ID</option>
        <option value="course_name">Course Name</option>
        <option value="created_at">Created Date</option>
        <option value="updated_at">Updated Date</option>
      </select>
      <button 
        class="btn btn-primary search-btn" 
        @click="$emit('search')"
      >
        <i class="fas fa-search"></i> Search
      </button>
      <button 
        v-if="isActive" 
        class="btn btn-secondary reset-btn" 
        @click="$emit('reset')"
      >
        <i class="fas fa-times"></i> Reset
      </button>
    </div>
    <div v-if="isActive" class="search-status">
      <span class="search-badge">
        <i class="fas fa-filter"></i> Filtered by: 
        {{ fieldLabel }}
      </span>
      <span class="search-query">"{{ modelValue }}"</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchBar',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    field: {
      type: String,
      default: 'all'
    },
    isActive: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'update:field', 'search', 'reset', 'input'],
  computed: {
    fieldLabel() {
      const fieldMap = {
        all: 'All Fields',
        id: 'ID',
        course_id: 'Course ID',
        course_name: 'Course Name',
        created_at: 'Created Date',
        updated_at: 'Updated Date'
      };
      return fieldMap[this.field] || this.field;
    }
  }
}
</script>

<style scoped>
.search-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  background-color: white;
  padding: 1.25rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.search-bar.active-search {
  border-left: 4px solid #3498db;
  background-color: #f8f9fa;
}

.search-input-container {
  position: relative;
  flex: 1;
  min-width: 250px;
}

.search-icon {
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
}

.search-input {
  padding: 0.75rem 0.875rem 0.75rem 2.5rem;
  width: 100%;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.search-input:focus {
  border-color: #3498db;
  outline: none;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.15);
}

.clear-search-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c757d;
  font-size: 1.25rem;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.clear-search-btn:hover {
  opacity: 1;
}

.search-filters {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.search-select {
  padding: 0.75rem 0.875rem;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  background-color: #f8f9fa;
  color: #343a40;
  font-size: 0.875rem;
  min-width: 140px;
  transition: all 0.2s ease;
}

.search-select:focus {
  border-color: #3498db;
  outline: none;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.15);
}

.btn {
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.btn-primary {
  background-color: #3498db;
  color: white;
}

.btn-primary:hover {
  background-color: #2980b9;
}

.btn-secondary {
  background-color: #f8f9fa;
  color: #343a40;
  border: 1px solid #dee2e6;
}

.btn-secondary:hover {
  background-color: #e9ecef;
}

.search-status {
  width: 100%;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px dashed #dee2e6;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
}

.search-badge {
  background-color: #e1f5fe;
  color: #0288d1;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-weight: 500;
}

.search-query {
  font-style: italic;
  color: #495057;
}

@media (max-width: 768px) {
  .search-bar {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .search-filters {
    width: 100%;
    justify-content: space-between;
  }
  
  .search-select {
    flex: 1;
  }
}
</style> 